import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchVanchiNames, updateVanchiName } from '../../store/slices/mastersSlice';
import { CreateVanchiNameRequest, VanchiNameCreatePayload, TableColumn, SimplePaginationInfo } from '../../types';
import DataTable from '../../common/components/DataTable';
import { mastersService } from '../../services/mastersService';
import { ValuesetParam } from '../../common/utils/valueset';
import { vanchiNameService } from '../../services/vanchiNameService';
import { toast } from '../../common/utils/toast';

const AddVanchiName: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { vanchiNames: vanchiNamesList, total, loading } = useAppSelector((state) => state.masters);
  const [nadaList, setNadaList] = useState<any[]>([]);
  const [vanchiList, setVanchiList] = useState<any[]>([]);
  const [nadaLoading, setNadaLoading] = useState<boolean>(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingVanchiName, setEditingVanchiName] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Validation schema
  const validationSchema = yup.object({
    nadaId: yup.string().required('Please select a Nada'),
    vanchiName: yup.string().required('Vanchi Name is required').min(2, 'Vanchi Name must be at least 2 characters'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CreateVanchiNameRequest>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      nadaId: '',
      vanchiName: '',
    },
  });

  const loadNada = async () => {
    try {
      setNadaLoading(true);
      const { data } = await mastersService.getValuesByParam(ValuesetParam.NADA);
      setNadaList(Array.isArray(data) ? data : []);
    } catch (_) {
      setNadaList([]);
    } finally {
      setNadaLoading(false);
    }
  };

  const loadVanchiList = async () => {
    try {
      const { data } = await vanchiNameService.getVanchiNames();
      setVanchiList(Array.isArray(data) ? data : []);
    } catch (_) {
      setVanchiList([]);
    }
  };

  useEffect(() => {
    dispatch(fetchVanchiNames());
    loadNada();
    loadVanchiList();
  }, [dispatch]);

  const handleEditClick = (vanchiName: any) => {
    setIsEditMode(true);
    setEditingVanchiName(vanchiName);
    setValue('nadaId', vanchiName.nadacd);
    setValue('vanchiName', vanchiName.vanchiname);
  };

  const handleReset = () => {
    reset({ nadaId: '', vanchiName: '' });
    setIsEditMode(false);
    setEditingVanchiName(null);
  };

  const onSubmit = async (data: CreateVanchiNameRequest) => {
    try {
      if (isEditMode && editingVanchiName) {
        // Edit mode - update existing vanchi name
        await dispatch(updateVanchiName({ 
          id: editingVanchiName.tblid, 
          data, 
          existingRowData: editingVanchiName, 
          nadaList, 
          userId: user?.userid 
        })).unwrap();
        toast.showSuccess('Vanchi updated successfully!');
      } else {
        // Create mode - create new vanchi name
        const createPayload = buildCreatePayload(data.nadaId, data.vanchiName);
        await vanchiNameService.createVanchiName(createPayload);
        toast.showSuccess('Vanchi created successfully!');
      }
      
      handleReset();
      loadVanchiList(); // Refresh the vanchi list
    } catch (err: any) {
      toast.showError(err?.message || 'Failed to save vanchi');
    }
  };

  const buildCreatePayload = (nadacd: string, vanchiname: string): VanchiNameCreatePayload => {
    // Find the selected NADA from the dropdown
    const selectedNada = nadaList.find(nada => nada.valuesetcode === nadacd);
    const nadaname = selectedNada?.valuesetnameen || selectedNada?.valuesetnameml || '';

    // Generate vanchicd by finding the highest value and incrementing
    let newVanchicd = 'VANCHI001'; // Default if no data
    if (vanchiList.length > 0) {
      const sortedCodes = vanchiList
        .map(item => item.vanchicd)
        .filter(code => code && code.startsWith('VANCHI'))
        .sort();
      
      if (sortedCodes.length > 0) {
        const lastCode = sortedCodes[sortedCodes.length - 1];
        const lastNumber = parseInt(lastCode.replace('VANCHI', '') || '0');
        newVanchicd = `VANCHI${String(lastNumber + 1).padStart(3, '0')}`;
      }
    }

    return {
      nadacd,
      nadaname,
      vanchicd: newVanchicd,
      vanchiname,
      isactive: 'Y',
      cretedby: user?.userid || 'SYSTEM'
    };
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      }) + ' / ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return '-';
    }
  };

  const columns: TableColumn[] = [
    {
      key: 'sNo',
      label: 'S.No.',
      sortable: true,
      width: 50,
      render: (_value: any, _row: any, rowIndex?: number, pagination?: any) => {
        const start = ((pagination?.page || 1) - 1) * (pagination?.limit || 0);
        return String((start + (rowIndex ?? 0) + 1)).padStart(2, '0');
      }
    },
    {
      key: 'nadaname',
      label: 'Nada Name',
      sortable: true,
      width: 120,
    },
    {
      key: 'vanchiname',
      label: 'Vanchi Name',
      sortable: true,
      width: 120,
    },
    {
      key: 'cretedby',
      label: 'Created By',
      sortable: true,
      width: 80,
    },
    {
      key: 'createddt',
      label: 'Creation Date/Time',
      sortable: true,
      width: 160,
      render: (value: string) => {
        if (!value) return '-';
        return formatDateTime(value);
      }
    },
    {
      key: 'edit',
      label: 'Edit',
      sortable: false,
      width: 50,
      isEditColumn: true,
      onEditClick: handleEditClick,
    }
  ];

  const pagination: SimplePaginationInfo = {
    page: currentPage,
    limit: 10,
  };

  return (
    <div className="h-100">
      <div className="row h-100">
        {/* Left Pane - Form (45%) */}
        <div className="col-lg-5 col-md-6">
          <div className="card h-100" style={{ background: '#e9e6ec' }}>
            <div className="card-header bg-primary text-secondary">
              <h6 className="mb-0">ADD VANCHI NAME</h6>
            </div>
            <div className="card-body d-flex flex-column h-100">
              <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column h-100">
                {/* Form Content - Flexible */}
                <div className="">
                  {/* Select Nada Field */}
                  <div className="mb-2">
                    <label className="form-label text-primary">SELECT NADA *</label>
                    <select
                      className={`form-select border-primary ${errors.nadaId ? 'is-invalid' : ''}`}
                      {...register('nadaId')}
                      disabled={nadaLoading}
                    >
                      <option value="">{nadaLoading ? 'Loading...' : 'Select Nada'}</option>
                      {nadaList.map((nada, index) => (
                        <option key={nada.valuesetcode || index} value={nada.valuesetcode}>
                          {nada.valuesetnameen || nada.valuesetnameml || nada.valuesetcode}
                        </option>
                      ))}
                    </select>
                    {errors.nadaId && (
                      <div className="invalid-feedback">{errors.nadaId.message}</div>
                    )}
                  </div>

                  {/* Vanchi Name Field */}
                  <div className="mb-4">
                    <label className="form-label text-primary">VANCHI NAME *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.vanchiName ? 'is-invalid' : ''}`}
                      placeholder="Enter New Vanchi Name"
                      {...register('vanchiName')}
                    />
                    {errors.vanchiName && (
                      <div className="invalid-feedback">{errors.vanchiName.message}</div>
                    )}
                  </div>
                </div>

                {/* Action Buttons - Fixed at Bottom */}
                <div className="d-flex justify-content-end">
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                    >
                      {isEditMode ? 'EDIT VANCHI' : 'ADD VANCHI'}
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                    >
                      CLEAR
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right Pane - Table (55%) */}
        <div className="col-lg-7 col-md-6">
          <div className="card h-100" style={{ background: '#e9e6ec' }}>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={vanchiNamesList}
                loading={loading}
                pagination={pagination}
                onPageChange={setCurrentPage}
                onSort={() => { }} // Sorting handled by Redux thunk in real app
                searchable={false}
                totalCountOverride={total}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVanchiName;